import type { ReactNode } from "react";

const pillars: { title: string; description: string; badge: string; badgeColor: string; iconColor: string; icon: ReactNode }[] = [
  {
    title: "Deploy",
    description: "AI agents for vertical workflows. Xeni (legal) and Robolab X (education) live. Agents process cases end-to-end with human QA.",
    badge: "LIVE",
    badgeColor: "bg-emerald-500/20 text-emerald-600",
    iconColor: "text-emerald-600",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>
    ),
  },
  {
    title: "Manage",
    description: "As agents become autonomous, orchestrate across teams. Identity, access, cost tracking per agent. Human oversight for exceptions.",
    badge: "BUILDING",
    badgeColor: "bg-orange-500/10 text-orange-500",
    iconColor: "text-orange-500",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" /><path d="M16 21v-5h5" /><circle cx="12" cy="12" r="3" /></svg>
    ),
  },
  {
    title: "Govern",
    description: "Full AI workforce at scale. Audit trails, compliance gates, quality monitoring. Human oversight at portfolio level.",
    badge: "VISION",
    badgeColor: "bg-violet-500/10 text-violet-600",
    iconColor: "text-violet-600",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><circle cx="15.5" cy="8.5" r="1.5" /><circle cx="15.5" cy="15.5" r="1.5" /><circle cx="8.5" cy="15.5" r="1.5" /></svg>
    ),
  },
];

export function SolutionSlide({ isActive: _ }: { isActive: boolean }) {
  return (
    <div className="flex flex-col h-full">
      <div className="mb-5 max-w-3xl">
        <h2 className="text-[32px] font-light tracking-tight mb-3 font-[family-name:var(--font-heading)]">
          COSX: The Agent Operating System<br />for regulated workflows
        </h2>
        <p className="text-[14px] text-[var(--color-fg-secondary)] leading-relaxed font-light">
          COSX gives regulated businesses a platform to deploy, manage, and govern AI agents — with built-in compliance, audit trails, and quality control. Not a chatbot. An operating system for your AI workforce.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4 mt-8">
        {pillars.map((p) => (
          <div key={p.title} className="flex flex-col border border-[var(--color-border)] rounded-xl p-5 bg-[var(--color-surface)] h-full">
            <div className="flex items-center gap-3 mb-3">
              <span className={`shrink-0 ${p.iconColor}`}>{p.icon}</span>
              <h3 className="text-[18px] font-medium font-[family-name:var(--font-heading)]">{p.title}</h3>
              <span className={`text-[9px] font-[family-name:var(--font-mono)] font-bold uppercase tracking-[0.1em] px-2 py-0.5 rounded-full ${p.badgeColor}`}>
                {p.badge}
              </span>
            </div>
            <p className="text-[12px] leading-relaxed text-[var(--color-fg-secondary)] font-light">{p.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
